import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { FC, useState } from 'react';
import { MaterializationAttemptStatus } from '../../../types/tecton_proto/data/materialization_status';
import { stageTypesToNiceNames } from './JobsUtils';
import TectonManageJobStatusBadge from './TectonManageJobStatusBadge';
import { StageEntryInterface } from './types';

import styled from '@emotion/styled';
import { ErrorPromptButtons } from '@tecton/ComponentRedesign';

import { Button, Code, Text, FlexItem, FlexGroup, Empty, ExtendedTableColumn, Table, Flyout } from '@lithos';

import { FlyoutContentProps } from '@tecton/ComponentRedesign/lib/ViewLayoutWithFlyoutPush';
import { secondsToHumanFriendlyString } from '../../../utils/misc-utils';
import { useTimezoneSettings } from '../../context/TimezoneContext';
import moment from 'moment/moment';
import { TectonDateTimeFormatWithTz } from '@tecton/ComponentRedesign/utils';

TimeAgo.addLocale(en);
const timeAgo = new TimeAgo('en-US');

interface JobStagesProps {
  materializationAttempts: StageEntryInterface[];
  materializationStatus?: MaterializationAttemptStatus;
}

const CodeWrapper = styled.div`
  .euiCodeBlock {
    height: calc(100vh - 168px);
    overflow: auto;
  }
`;

const JobStages: FC<JobStagesProps> = ({ materializationAttempts = [], materializationStatus }) => {
  const { timezone } = useTimezoneSettings();
  const [, setSelectedStage] = useState<StageEntryInterface | undefined>();
  const [isFlyoutOpen, setIsFlyoutOpen] = useState<boolean>(false);
  const [flyoutContent, setFlyoutContent] = useState<FlyoutContentProps | undefined>();

  const createdAt = materializationStatus?.attempt_created_at
    ? new Date(materializationStatus.attempt_created_at)
    : new Date();

  const columns: ExtendedTableColumn<StageEntryInterface & { id: string }>[] = [
    {
      name: 'Status',
      id: 'status',
      content: (stage: StageEntryInterface) => {
        const asStatus = stage.state;
        return (
          <>
            <TectonManageJobStatusBadge status={asStatus} />
          </>
        );
      },
    },
    {
      name: 'Started At',
      id: 'startedAt',
      content: (stage: StageEntryInterface) => {
        if (!stage.createdAt) {
          return <>N/A</>;
        }

        return <>{TectonDateTimeFormatWithTz(moment(stage.createdAt), timezone)}</>;
      },
    },
    {
      name: 'Completed At',
      id: 'completedAt',
      content: (stage: StageEntryInterface) => {
        if (!stage.isTerminal || !stage.endedAt) {
          return <>N/A</>;
        }

        return <>{TectonDateTimeFormatWithTz(moment(stage.endedAt), timezone)}</>;
      },
    },
    {
      name: 'Duration',
      id: 'duration',
      content: (stage: StageEntryInterface) => {
        return <>{secondsToHumanFriendlyString(stage.durationSeconds)}</>;
      },
    },
    {
      name: 'Details',
      id: 'details',
      content: (stage: StageEntryInterface) => {
        return <>{stage.description}</>;
      },
    },
    {
      name: 'Actions',
      id: 'actions',
      columnWidth: 'm',
      content: (stage: StageEntryInterface) => {
        return stage?.errorDetail ? (
          <>
            <Button
              icon="Documentation"
              type="secondary"
              onClick={() => {
                setSelectedStage(stage);
                setIsFlyoutOpen(true);
                const headerContent = (
                  <>
                    {' '}
                    <Text size="l" weight="medium">
                      {stageTypesToNiceNames[stage?.type ?? 'PYTHON']}
                    </Text>{' '}
                    {stage?.state && <TectonManageJobStatusBadge status={stage.state} />}
                  </>
                );

                const bodyContent = (
                  <>
                    <FlexGroup gapSize="4" direction="column">
                      <FlexItem grow={0}>{stage?.description}</FlexItem>
                      <FlexItem grow={0}>
                        <CodeWrapper>
                          <Code code={stage?.errorDetail ?? ''} />
                        </CodeWrapper>
                      </FlexItem>
                    </FlexGroup>
                  </>
                );

                setFlyoutContent({
                  headerContent: headerContent,
                  bodyContent: bodyContent,
                  onCloseClicked: () => {
                    setFlyoutContent(undefined);
                  },
                });
              }}
            />
          </>
        ) : (
          <></>
        );
      },
    },
  ];

  return (
    <>
      <Flyout
        isOpen={isFlyoutOpen}
        setIsOpen={setIsFlyoutOpen}
        title={flyoutContent?.headerContent}
        content={flyoutContent?.bodyContent}
      />

      {materializationAttempts.length === 0 ? (
        <Empty
          title={<h2>No Job Stages Available</h2>}
          description={<p>Please try again later.</p>}
          placement="embedded"
          useCase="errorKnown"
          actions={undefined}
        />
      ) : (
        <>
          <FlexGroup gapSize="4" direction="column" padding="0">
            <FlexItem grow={0} shrink={1}>
              Last Updated {timeAgo.format(createdAt)}
            </FlexItem>
            <FlexItem grow={1} shrink={0}>
              <>
                <Table
                  items={materializationAttempts.map((attempt) => {
                    return { ...attempt, id: attempt.compiledQuery ?? '' };
                  })}
                  columns={columns}
                  emptyProps={{
                    placement: 'embedded',
                    useCase: 'errorKnown',
                    title: 'Job',
                    description: 'Please try again later.',
                    actions: <ErrorPromptButtons />,
                  }}
                />
              </>
            </FlexItem>
          </FlexGroup>
        </>
      )}
    </>
  );
};

export default JobStages;
